import { useLazyQuery, useQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";

import CircleButton from "@components/buttons/CircleButton";
import styles from "@components/explore/Explore.module.css";
import HandleQuery from "@components/HandleQuery";
import ExploreHeaderLoader from "@components/loaders/ExploreHeaderLoader";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ExternalLink from "@components/typography/ExternalLink";
import Heading1 from "@components/typography/Heading1";
import Heading2 from "@components/typography/Heading2";
import InternalLink from "@components/typography/InternalLink";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import { ExploreSectionsEnum } from "@typing/Enums";
import {
  AppEventActionKindEnum,
  AppEventCreateDocument,
  AppEventCreateMutation,
  AppEventCreateMutationVariables,
  BannerDismissDocument,
  BannerDismissMutation,
  BannerDismissMutationVariables,
  BannerKindEnum,
  GetClientWithBannersDocument,
  GetClientWithBannersQuery,
  GetClientWithBannersQueryVariables,
  GetJourneyWithExploreDocument,
  GetJourneyWithExploreQuery,
  GetJourneyWithExploreQueryVariables
} from "@typing/Generated";
import { imageUrl } from "@utils/imageUtils";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  exploreSection: ExploreSectionsEnum;
  journeyId: string;
};

type HeaderCardProps = {
  journeyId: string;
  section: ExploreSectionsEnum;
  selectedSection: ExploreSectionsEnum;
};

const HeaderCard = ({ journeyId, section, selectedSection }: HeaderCardProps) => {
  const myClient = useMyClient();
  const imgClassName = styles[`${section}HeaderImg`] + " " + styles.hideMobile;

  const getIcon = (section: ExploreSectionsEnum) => {
    switch (section) {
      case ExploreSectionsEnum.MARKETPLACE:
        return "store";
      case ExploreSectionsEnum.COMMUNITY:
        return "peopleGroup";
      case ExploreSectionsEnum.ARTICLES:
        return "photoFilm";
      default:
        return "calendarStar";
    }
  };

  const [createAppEvent] = useMutationWithValidation<AppEventCreateMutation, AppEventCreateMutationVariables>(
    AppEventCreateDocument,
    "appEvent"
  );

  const renderGuts = () => (
    <>
      <div className={`${styles.headerCardContainer} ${styles[`${section}HeaderContainer`]}`}>
        <div className={styles.headerImgContainer}>
          <div>
            <img className={imgClassName} src={imageUrl(section)} />
          </div>

          <div className={`${styles.iconContainer} ${styles.showMobile}`}>
            <Icon color={section === selectedSection ? "white" : "primary-500"} icon={getIcon(section)} />
          </div>
        </div>

        <div className={`${styles.headerText} ${styles.showMobile}`}>
          <BodyCopy bold color={selectedSection === section ? "white" : "primary-500"}>
            <FormattedMessage id={`pages.explore.${section}.title`} />
          </BodyCopy>
        </div>
      </div>
      <div className={`${styles.headerText} ${styles.hideMobile}`}>
        <Heading2 bold={false} color="primary">
          <FormattedMessage id={`pages.explore.${section}.title`} />
        </Heading2>
      </div>
    </>
  );

  return (
    <div className={styles.flexBasis}>
      {section === ExploreSectionsEnum.COMMUNITY ? (
        <ExternalLink
          className={section === selectedSection ? styles.headerCardSelected : styles.headerCard}
          expand
          href="https://gather.withgrayce.com"
          linkEventCreateVariables={{ journeyId }}
          onClick={() => {
            createAppEvent({
              actionKind: AppEventActionKindEnum.COMMUNITY_OPENED
            });
          }}
          stylized={false}
        >
          {renderGuts()}
        </ExternalLink>
      ) : (
        <InternalLink
          className={section === selectedSection ? styles.headerCardSelected : styles.headerCard}
          expand
          linkEventCreateVariables={{ journeyId }}
          stylized={false}
          to={
            section === ExploreSectionsEnum.ARTICLES
              ? journeyRoute({ journeyId, linkAfterJourney: "/explore", myClient })
              : journeyRoute({ journeyId, linkAfterJourney: `/explore/${section}`, myClient })
          }
        >
          {renderGuts()}
        </InternalLink>
      )}
    </div>
  );
};

const ExploreHeader = ({ exploreSection, journeyId }: Props) => {
  const myClient = useMyClient();

  const journeyQuery = useQuery<GetJourneyWithExploreQuery, GetJourneyWithExploreQueryVariables>(
    GetJourneyWithExploreDocument,
    {
      variables: {
        id: journeyId
      }
    }
  );

  const [getClient, clientQuery] = useLazyQuery<GetClientWithBannersQuery, GetClientWithBannersQueryVariables>(
    GetClientWithBannersDocument
  );

  useEffect(() => {
    if (myClient) {
      getClient({
        variables: {
          id: myClient.id
        }
      });
    }
  }, [getClient, myClient]);

  const [dismissBanner] = useMutationWithValidation<BannerDismissMutation, BannerDismissMutationVariables>(
    BannerDismissDocument,
    "banner"
  );

  const bannerForExploreSection = useMemo(() => {
    const bannerKindForExploreSection =
      exploreSection === ExploreSectionsEnum.EVENTS
        ? BannerKindEnum.EXPLORE_EVENTS
        : exploreSection === ExploreSectionsEnum.MARKETPLACE
          ? BannerKindEnum.EXPLORE_MARKETPLACE
          : null;

    return clientQuery.data?.client.banners.find(
      banner => banner.kind === bannerKindForExploreSection && !banner.dismissedAt
    );
  }, [clientQuery.data?.client.banners, exploreSection]);

  return (
    <div>
      <div>
        <div className={`${styles.headerTitle} ${styles.hideMobile}`}>
          <Heading1>
            <FormattedMessage id="pages.explore.exploreResources" />
          </Heading1>
        </div>
        <HandleQuery loadingContent={<ExploreHeaderLoader />} query={journeyQuery}>
          {data => (
            <div className={styles.headerCards}>
              <HeaderCard
                journeyId={journeyId}
                section={ExploreSectionsEnum.ARTICLES}
                selectedSection={exploreSection}
              />
              {data.journey.clientEmployerBenefitsCount > 0 && (
                <HeaderCard
                  journeyId={journeyId}
                  section={ExploreSectionsEnum.MARKETPLACE}
                  selectedSection={exploreSection}
                />
              )}
              <HeaderCard
                journeyId={journeyId}
                section={ExploreSectionsEnum.COMMUNITY}
                selectedSection={exploreSection}
              />
              <HeaderCard journeyId={journeyId} section={ExploreSectionsEnum.EVENTS} selectedSection={exploreSection} />
            </div>
          )}
        </HandleQuery>
      </div>
      {exploreSection !== ExploreSectionsEnum.ARTICLES && (
        <div>
          {bannerForExploreSection && (
            <div className={styles.headerBanner + " " + styles.largeSpacingBottom} data-test="explore-banner">
              <div>
                <Heading1 color="white">
                  <FormattedMessage id={`pages.explore.${exploreSection}.bannerTitle`} />
                </Heading1>
                <div className={styles.headerBannerDescription}>
                  <div className={styles.showMobile}>
                    <BodyCopy bold color="white">
                      <FormattedMessage id={`pages.explore.${exploreSection}.bannerDescription`} />
                    </BodyCopy>
                  </div>

                  <div className={styles.hideMobile}>
                    <Heading2 bold={false} color="white">
                      <FormattedMessage id={`pages.explore.${exploreSection}.bannerDescription`} />
                    </Heading2>
                  </div>
                </div>
              </div>
              <div className={styles.headerBannerClose}>
                <CircleButton
                  ariaLabelKey="accessibility.ariaLabels.close"
                  color="white"
                  data-test="close-banner"
                  icon="xmark"
                  onClick={() => dismissBanner({ id: bannerForExploreSection.id })}
                  size="medium"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExploreHeader;
